import {
  FIELD_TYPES,
  METRIC_AGGREGATION_POLICIES,
} from "./fieldClassification";
import {
  getFallbackSeriesColor,
  getMetricSeriesColors,
  getTemporalSeriesColor,
} from "./visualizationColors";
import { getCategoricalLayout } from "./visualizationLayout";

const METADATA_FIELDS = new Set([
  "rowid",
  "row_id",
  "freshness",
  "operation_data",
  "trace_metadata",
  "selected_rows",
  "candidate_rows",
  "source",
  "source_id",
  "source_metadata",
  "query_trace",
  "response_status",
  "data_notes",
  "debug",
  "metadata",
]);

const NON_METRIC_NUMERIC_FIELDS = new Set([
  "year",
  "reporting_year",
  "round",
  "round_number",
  "freshness",
]);

const CONTEXT_FIELDS = new Set([
  "admin_0_name",
  "country",
  "country_name",
  "operation",
  "operation_name",
  "round",
  "round_number",
  "reporting_date",
  "reporting_year",
  "date",
  "year",
  "year_month",
  "month_year",
  "period",
]);
const COUNTRY_FIELD_NAMES = new Set([
  "admin_0_name",
  "country",
  "country_name",
]);

const OPERATION_FIELD_NAMES = new Set([
  "operation",
  "operation_name",
]);

const KNOWN_ADDITIVE_METRICS = new Set([
  "total_idps",
  "num_present_idp_ind",
  "male_idps",
  "female_idps",
]);

const NON_ADDITIVE_METRIC_PATTERN =
  /(?:^|_)(?:pct|percent|percentage|rate|ratio|share|proportion|average|avg|mean|median|freshness|year|round|rank|ranking|position|score|index|identifier|id|code|pcode)(?:_|$)/;

const ADDITIVE_METRIC_PATTERN =
  /(?:^|_)(?:count|caseload|persons|people|individuals|households|families|beneficiaries|idps?|returnees?)(?:_|$)/;


const FILTER_PRIORITY = [
  "admin_0_name",
  "country",
  "operation",
  "year",
  "reporting_year",
  "reporting_date",
  "date",
  "round_number",
  "round",
];
const SUPPORTED_VIEW_BY_FIELDS = new Set([
  "admin_1_name",
  "admin1_name",
  "admin_1",
  "admin1",
  "admin_1_name_en",
  "admin_2_name",
  "admin2_name",
  "admin_2",
  "admin2",
  "admin_2_name_en",
  "reason_for_displacement",
  "displacement_reason",
  "reason",
  "idp_origin_admin_1",
  "origin_admin_1_name",
  "origin_admin1_name",
  "admin_1_origin_name",
  "admin1_origin_name",
  "origin_admin_1",
  "origin_admin1",
  "origin_admin_1_name_en",
  "origin_name",
  "origin",
]);
const VIEW_BY_PRIORITY = [
  "admin_2_name",
  "admin2_name",
  "admin_2",
  "admin2",
  "admin_2_name_en",
  "admin_1_name",
  "admin1_name",
  "admin_1",
  "admin1",
  "admin_1_name_en",
  "reason_for_displacement",
  "displacement_reason",
  "reason",
  "idp_origin_admin_1",
  "origin_admin_1_name",
  "origin_admin1_name",
  "admin_1_origin_name",
  "admin1_origin_name",
  "origin_admin_1",
  "origin_admin1",
  "origin_admin_1_name_en",
  "origin_name",
  "origin",
];
const TOTAL_METRIC_PRIORITY = [
  "total_idps",
  "num_present_idp_ind",
  "total",
  "value",
];
const UNAVAILABLE_LABELS = new Set([
  "unknown",
  "not available",
  "n/a",
  "na",
  "undefined",
  "null",
]);

export const normalizeFieldName = (field) =>
  String(field || "").trim().toLowerCase();

export const isSupportedViewByField = (field) =>
  SUPPORTED_VIEW_BY_FIELDS.has(normalizeFieldName(field));

export const normalizeCategoryValue = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("en-US");

export const isMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const normalized = normalizeCategoryValue(value);
    return Boolean(normalized) && !UNAVAILABLE_LABELS.has(normalized);
  }
  return ["number", "boolean"].includes(typeof value);
};

export const isPrimitiveScalar = (value) =>
  value === null ||
  value === undefined ||
  ["string", "number", "boolean"].includes(typeof value);

export const isMetadataField = (field) => {
  const normalized = normalizeFieldName(field);
  return (
    METADATA_FIELDS.has(normalized) ||
    /(?:^|_)freshness$/.test(normalized) ||
    /_(?:id|pcode|code)$/.test(normalized) ||
    /^(?:trace|source|metadata|debug)_/.test(normalized)
  );
};

const normalizeConfiguredFields = (fields) =>
  new Set((Array.isArray(fields) ? fields : []).map(normalizeFieldName));

const isContextName = (field) => CONTEXT_FIELDS.has(normalizeFieldName(field));

const isKnownAdditiveMetric = (field, configuredAdditiveMetrics) => {
  const normalized = normalizeFieldName(field);
  if (configuredAdditiveMetrics.has(normalized)) return true;
  if (KNOWN_ADDITIVE_METRICS.has(normalized)) return true;
  return (
    ADDITIVE_METRIC_PATTERN.test(normalized) &&
    !NON_ADDITIVE_METRIC_PATTERN.test(normalized)
  );
};

export const isRoundField = (field) =>
  /(?:^|_)round(?:_number)?$/.test(normalizeFieldName(field));

const getTemporalPriority = (field) => {
  const normalized = normalizeFieldName(field);
  if (isRoundField(normalized)) return Number.MAX_SAFE_INTEGER;
  if (/(?:^|_)reporting_date$/.test(normalized)) return 0;
  if (/(?:^|_)date$/.test(normalized)) return 1;
  if (
    /(?:^|_)(?:year_month|month_year|month_period|period_month|reporting_month|reporting_period|period)$/.test(
      normalized,
    )
  ) return 2;
  if (/(?:^|_)(?:reporting_)?year$/.test(normalized)) return 3;
  return Number.MAX_SAFE_INTEGER;
};

const isTemporalName = (field) =>
  getTemporalPriority(field) !== Number.MAX_SAFE_INTEGER;

const isValidCalendarDate = (year, month = 1, day = 1) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

export const parseTemporalValue = (value, field = "") => {
  if (!isMeaningfulValue(value)) return null;
  if (typeof value === "number") {
    if (!/(?:^|_)year$/.test(normalizeFieldName(field))) return null;
    if (!Number.isInteger(value) || value < 1000 || value > 9999) return null;
    return Date.UTC(value, 0, 1);
  }

  const text = String(value).trim();
  let match = text.match(/^(\d{4})$/);
  if (match) return Date.UTC(Number(match[1]), 0, 1);

  match = text.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    return isValidCalendarDate(year, month) ? Date.UTC(year, month - 1, 1) : null;
  }

  match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/,
  );
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isValidCalendarDate(year, month, day)) return null;
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : Date.UTC(year, month - 1, day);
};

export const formatFieldLabel = (field) =>
  String(field || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .replace(/\bIdps\b/g, "IDPs")
    .replace(/\bIdp\b/g, "IDP");

const collectFieldNames = (rows) => {
  const fields = new Set();
  rows.forEach((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) return;
    Object.keys(row).forEach((field) => fields.add(field));
  });
  return Array.from(fields);
};

export const getMeaningfulUniqueValues = (rows, field) => {
  const values = new Map();
  rows.forEach((row) => {
    const value = row?.[field];
    if (!isMeaningfulValue(value) || !isPrimitiveScalar(value)) return;
    const key = `${typeof value}:${normalizeCategoryValue(value)}`;
    if (!values.has(key)) values.set(key, value);
  });
  return Array.from(values.values());
};

export const hasFiniteMetricValue = (rows, field) =>
  rows.some(
    (row) => typeof row?.[field] === "number" && Number.isFinite(row[field]),
  );

export const classifyFields = (rows, options = {}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const configuredTemporal = normalizeFieldName(options.temporalField);
  const configuredContext = normalizeConfiguredFields(options.contextFields);
  const configuredBreakdown = normalizeConfiguredFields(options.breakdownFields);
  const configuredAdditiveMetrics = normalizeConfiguredFields(options.additiveMetrics);
  const configuredNonAdditiveMetrics = normalizeConfiguredFields(
    options.nonAdditiveMetrics,
  );

  return collectFieldNames(safeRows).map((field, sourceIndex) => {
    const values = safeRows
      .map((row) => row?.[field])
      .filter((value) => value !== null && value !== undefined);
    const normalizedField = normalizeFieldName(field);
    let type = FIELD_TYPES.BREAKDOWN;
    let reason = "user_facing_breakdown";

    if (isMetadataField(field)) {
      type = FIELD_TYPES.METADATA;
      reason = "technical_field";
    } else if (values.some((value) => !isPrimitiveScalar(value))) {
      type = FIELD_TYPES.UNSUPPORTED;
      reason = "structured_value";
    } else if (
      (configuredTemporal &&
        normalizedField === configuredTemporal &&
        !isRoundField(field)) ||
      (isTemporalName(field) &&
        values.some(isMeaningfulValue) &&
        values
          .filter(isMeaningfulValue)
          .every((value) => parseTemporalValue(value, field) !== null))
    ) {
      type = FIELD_TYPES.TEMPORAL;
      reason = "strict_temporal_values";
    } else if (
      configuredContext.has(normalizedField) ||
      (!configuredBreakdown.has(normalizedField) && isContextName(field))
    ) {
      type = FIELD_TYPES.CONTEXT;
      reason = configuredContext.has(normalizedField)
        ? "configured_context_dimension"
        : "known_context_dimension";
    } else if (
      !NON_METRIC_NUMERIC_FIELDS.has(normalizedField) &&
      hasFiniteMetricValue(safeRows, field)
    ) {
      const additive =
        !configuredNonAdditiveMetrics.has(normalizedField) &&
        isKnownAdditiveMetric(field, configuredAdditiveMetrics);
      type = additive
        ? FIELD_TYPES.ADDITIVE_METRIC
        : FIELD_TYPES.NON_ADDITIVE_METRIC;
      reason = additive ? "known_additive_count" : "additivity_not_established";
    }

    return {
      field,
      type,
      reason,
      sourceIndex,
      values: getMeaningfulUniqueValues(safeRows, field),
    };
  });
};

const getDistinctValidTemporalValues = (rows, classification) => {
  const sourceRows = Array.isArray(rows) ? rows : null;
  const values = sourceRows
    ? sourceRows.map((row) => row?.[classification.field])
    : classification.values;
  return new Set(
    (values || [])
      .map((value) => parseTemporalValue(value, classification.field))
      .filter((value) => value !== null),
  );
};

export const detectTemporalField = (classifications, configuredField, rows = null) => {
  const temporal = classifications.filter(
    (classification) =>
      classification.type === FIELD_TYPES.TEMPORAL &&
      !isRoundField(classification.field) &&
      getDistinctValidTemporalValues(rows, classification).size > 1,
  );
  if (!temporal.length) return null;
  const configured = normalizeFieldName(configuredField);

  return temporal.slice().sort((left, right) => {
    const priorityDifference =
      getTemporalPriority(left.field) - getTemporalPriority(right.field);
    if (priorityDifference) return priorityDifference;
    const leftConfigured = normalizeFieldName(left.field) === configured ? 0 : 1;
    const rightConfigured = normalizeFieldName(right.field) === configured ? 0 : 1;
    return leftConfigured - rightConfigured || left.sourceIndex - right.sourceIndex;
  })[0]?.field ?? null;
};

const priorityIndex = (field, priorities) => {
  const index = priorities.indexOf(normalizeFieldName(field));
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const compareRoundValues = (left, right) => {
  const leftNumber = Number(String(left).match(/\d+(?:\.\d+)?/)?.[0]);
  const rightNumber = Number(String(right).match(/\d+(?:\.\d+)?/)?.[0]);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const sortDimensionValues = (field, values) =>
  values.slice().sort((left, right) => {
    if (/round/.test(normalizeFieldName(field))) {
      return compareRoundValues(left, right);
    }
    const leftTime = parseTemporalValue(left, field);
    const rightTime = parseTemporalValue(right, field);
    if (leftTime !== null && rightTime !== null) return leftTime - rightTime;
    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });

export const buildFilterFields = (rows, classifications) =>
  classifications
    .filter(
      ({ type, field }) =>
        [FIELD_TYPES.CONTEXT, FIELD_TYPES.TEMPORAL].includes(type) &&
        getMeaningfulUniqueValues(rows, field).length > 1,
    )
    .slice()
    .sort(
      (left, right) =>
        priorityIndex(left.field, FILTER_PRIORITY) -
          priorityIndex(right.field, FILTER_PRIORITY) ||
        left.sourceIndex - right.sourceIndex,
    )
    .map(({ field }) => field);

export const applyFilters = (rows, filters, ignoredField = null) =>
  rows.filter((row) =>
    Object.entries(filters || {}).every(([field, selected]) => {
      if (field === ignoredField || !isMeaningfulValue(selected)) return true;
      return normalizeCategoryValue(row?.[field]) === normalizeCategoryValue(selected);
    }),
  );

export const resolveCascadingFilters = (
  rows,
  filterFields,
  filters,
  ignoredFields = null,
) => {
  const resolved = {};
  const definitions = [];
  const ignored = new Set(
    (Array.isArray(ignoredFields) ? ignoredFields : [ignoredFields]).filter(Boolean),
  );

  filterFields.forEach((field) => {
    if (ignored.has(field)) return;
    const upstreamRows = applyFilters(rows, resolved);
    const options = sortDimensionValues(
      field,
      getMeaningfulUniqueValues(upstreamRows, field),
    );
    const selected = filters?.[field];
    const matchingOption = options.find(
      (option) =>
        isMeaningfulValue(selected) &&
        normalizeCategoryValue(option) === normalizeCategoryValue(selected),
    );
    if (matchingOption !== undefined) resolved[field] = matchingOption;
    else if (options.length) resolved[field] = options[0];
    if (options.length > 1) definitions.push({ field, options });
  });

  return { filters: resolved, definitions };
};

export const getViewByFields = (rows, classifications, temporalField) =>
  classifications
    .filter(
      ({ type, field }) =>
        type === FIELD_TYPES.BREAKDOWN &&
        isSupportedViewByField(field) &&
        field !== temporalField &&
        getMeaningfulUniqueValues(rows, field).length > 1,
    )
    .slice()
    .sort((left, right) => {
      const cardinalityDifference =
        getMeaningfulUniqueValues(rows, right.field).length -
        getMeaningfulUniqueValues(rows, left.field).length;
      return (
        cardinalityDifference ||
        priorityIndex(left.field, VIEW_BY_PRIORITY) -
          priorityIndex(right.field, VIEW_BY_PRIORITY) ||
        left.sourceIndex - right.sourceIndex
      );
    })
    .map(({ field }) => field);

export const getMetricFields = (rows, classifications) =>
  classifications
    .filter(
      ({ type, field }) =>
        [FIELD_TYPES.ADDITIVE_METRIC, FIELD_TYPES.NON_ADDITIVE_METRIC].includes(
          type,
        ) && hasFiniteMetricValue(rows, field),
    )
    .map(({ field }) => field);

export const selectDefaultMetric = (metrics, preferredMetric) => {
  const normalizedMetrics = new Map(
    metrics.map((metric) => [normalizeFieldName(metric), metric]),
  );
  return (
    normalizedMetrics.get(normalizeFieldName(preferredMetric)) ||
    TOTAL_METRIC_PRIORITY.map((field) => normalizedMetrics.get(field)).find(Boolean) ||
    metrics[0] ||
    null
  );
};

export const selectDefaultViewBy = (fields) => fields[0] || null;

export const determineChartMode = ({ rows, temporalField, viewBy }) => {
  const temporalValues = temporalField
    ? getMeaningfulUniqueValues(rows, temporalField).filter(
        (value) => parseTemporalValue(value, temporalField) !== null,
      )
    : [];
  if (temporalField && temporalValues.length >= 2) return "temporal";
  return viewBy ? "categorical" : "single_value";
};

export const getRedundantTemporalFields = (classifications, temporalField) => {
  const priority = getTemporalPriority(temporalField);
  if (priority > 2) return [];
  return classifications
    .filter(
      ({ type, field }) =>
        type === FIELD_TYPES.TEMPORAL &&
        field !== temporalField &&
        getTemporalPriority(field) > priority,
    )
    .map(({ field }) => field);
};

export const isRoundFilterRequired = ({ rows, roundField, viewBy, temporalField }) => {
  if (!roundField || getMeaningfulUniqueValues(rows, roundField).length <= 1) return false;
  if (!temporalField) return true;

  const roundsByCoordinate = new Map();
  rows.forEach((row) => {
    if (
      !isMeaningfulValue(row?.[roundField]) ||
      parseTemporalValue(row?.[temporalField], temporalField) === null
    ) return;
    const key =
      normalizeCategoryValue(row[temporalField]) +
      "\u0000" +
      normalizeCategoryValue(viewBy ? row?.[viewBy] : "single-series");
    const rounds = roundsByCoordinate.get(key) || new Set();
    rounds.add(normalizeCategoryValue(row[roundField]));
    roundsByCoordinate.set(key, rounds);
  });
  return Array.from(roundsByCoordinate.values()).some((rounds) => rounds.size > 1);
};


export const getMetricAggregationPolicy = (fieldName, fieldClassification) => {
  const classification = Array.isArray(fieldClassification)
    ? fieldClassification.find(({ field }) => field === fieldName)
    : fieldClassification;
  return classification?.type === FIELD_TYPES.ADDITIVE_METRIC
    ? METRIC_AGGREGATION_POLICIES.SUM
    : METRIC_AGGREGATION_POLICIES.NONE;
};
const findFieldByNames = (classifications, names) =>
  classifications.find(({ field }) => names.has(normalizeFieldName(field)))?.field || null;

const getComparisonTemporalFields = (rows, classifications) =>
  classifications
    .filter(
      ({ field }) =>
        !isRoundField(field) &&
        isTemporalName(field) &&
        getMeaningfulUniqueValues(rows, field).length > 0,
    )
    .slice()
    .sort(
      (left, right) =>
        getTemporalPriority(left.field) - getTemporalPriority(right.field) ||
        left.sourceIndex - right.sourceIndex,
    )
    .map(({ field }) => field);

export const getAutomaticCountryComparison = ({ rows, classifications, metrics }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const countryField = findFieldByNames(classifications, COUNTRY_FIELD_NAMES);
  if (!countryField) return { eligible: false, categoryField: null, reason: "missing_country" };

  const countryValues = getMeaningfulUniqueValues(safeRows, countryField);
  if (countryValues.length <= 1) {
    return { eligible: false, categoryField: null, reason: "single_country" };
  }

  const varyingBreakdown = classifications.find(
    ({ field }) =>
      isSupportedViewByField(field) &&
      getMeaningfulUniqueValues(safeRows, field).length > 1,
  );
  if (varyingBreakdown) {
    return { eligible: false, categoryField: null, reason: "varying_breakdown", unresolvedField: varyingBreakdown.field };
  }

  const operationField = findFieldByNames(classifications, OPERATION_FIELD_NAMES);
  if (operationField && getMeaningfulUniqueValues(safeRows, operationField).length > 1) {
    return { eligible: false, categoryField: null, reason: "unresolved_operation", unresolvedField: operationField };
  }

  const varyingContext = classifications.find(
    ({ type, field }) =>
      [FIELD_TYPES.CONTEXT, FIELD_TYPES.DIMENSION].includes(type) &&
      field !== countryField &&
      field !== operationField &&
      getMeaningfulUniqueValues(safeRows, field).length > 1,
  );
  if (varyingContext) {
    return { eligible: false, categoryField: null, reason: "unresolved_context", unresolvedField: varyingContext.field };
  }

  const temporalFields = getComparisonTemporalFields(safeRows, classifications);
  if (!temporalFields.length) {
    return { eligible: false, categoryField: null, reason: "missing_timeframe" };
  }

  for (const field of temporalFields) {
    const parsedValues = safeRows.map((row) => parseTemporalValue(row?.[field], field));
    if (parsedValues.some((value) => value === null) || new Set(parsedValues).size !== 1) {
      return { eligible: false, categoryField: null, reason: "incomparable_timeframe", unresolvedField: field };
    }
  }

  const visibleMetrics = (Array.isArray(metrics) ? metrics : []).filter(Boolean);
  if (!visibleMetrics.length) {
    return { eligible: false, categoryField: null, reason: "missing_metric" };
  }

  for (const metric of visibleMetrics) {
    const policy = getMetricAggregationPolicy(metric, classifications);
    for (const country of countryValues) {
      const coordinateRows = safeRows.filter(
        (row) =>
          normalizeCategoryValue(row?.[countryField]) === normalizeCategoryValue(country) &&
          typeof row?.[metric] === "number" &&
          Number.isFinite(row[metric]),
      );
      if (!coordinateRows.length) {
        return { eligible: false, categoryField: null, reason: "missing_country_metric", unresolvedField: metric };
      }
      if (coordinateRows.length > 1 && policy !== METRIC_AGGREGATION_POLICIES.SUM) {
        return { eligible: false, categoryField: null, reason: "unsafe_metric_coordinate", unresolvedField: metric };
      }
    }
  }

  return {
    eligible: true,
    categoryField: countryField,
    timeframeField: temporalFields[0],
    reason: null,
  };
};

export const validateChartCoordinates = ({
  rows,
  classifications,
  viewBy,
  metric,
  mode,
  temporalField,
  aggregationPolicy,
  ignoredContextFields = [],
}) => {
  if (!metric) return { valid: true, unresolvedField: null };
  const ignoredContext = new Set(ignoredContextFields.filter(Boolean));

  const unresolvedContext = classifications.find(
    ({ type, field }) =>
      [FIELD_TYPES.CONTEXT, FIELD_TYPES.TEMPORAL].includes(type) &&
      field !== viewBy &&
      !ignoredContext.has(field) &&
      !(mode === "temporal" && field === temporalField) &&
      getMeaningfulUniqueValues(rows, field).length > 1,
  )?.field;
  if (unresolvedContext) {
    return { valid: false, unresolvedField: unresolvedContext };
  }

  const coordinateRows = new Map();
  rows.forEach((row) => {
    if (viewBy && !isMeaningfulValue(row?.[viewBy])) return;
    if (typeof row?.[metric] !== "number" || !Number.isFinite(row[metric])) return;
    const temporalValue = mode === "temporal" ? row?.[temporalField] : "single-period";
    if (mode === "temporal" && parseTemporalValue(temporalValue, temporalField) === null) return;
    const key =
      normalizeCategoryValue(viewBy ? row?.[viewBy] : "overall") +
      "\u0000" +
      normalizeCategoryValue(temporalValue);
    const grouped = coordinateRows.get(key) || [];
    grouped.push(row);
    coordinateRows.set(key, grouped);
  });

  const duplicateRows = Array.from(coordinateRows.values()).find(
    (grouped) => grouped.length > 1,
  );
  if (!duplicateRows) return { valid: true, unresolvedField: null };

  const policy = aggregationPolicy || getMetricAggregationPolicy(metric, classifications);
  if (policy === METRIC_AGGREGATION_POLICIES.SUM) {
    return { valid: true, unresolvedField: null };
  }

  const unresolvedField = classifications.find(
    ({ type, field }) =>
      [FIELD_TYPES.CONTEXT, FIELD_TYPES.TEMPORAL].includes(type) &&
      field !== viewBy &&
      field !== temporalField &&
      !ignoredContext.has(field) &&
      getMeaningfulUniqueValues(duplicateRows, field).length > 1,
  )?.field;

  return { valid: false, unresolvedField: unresolvedField || null };
};

export const buildVisualizationDataset = ({
  rows,
  mode,
  viewBy,
  metric,
  temporalField,
  aggregationPolicy,
}) => {
  const validRows = rows.filter(
    (row) =>
      (!viewBy || isMeaningfulValue(row?.[viewBy])) &&
      typeof row?.[metric] === "number" &&
      Number.isFinite(row[metric]) &&
      (mode !== "temporal" ||
        parseTemporalValue(row?.[temporalField], temporalField) !== null),
  );

  if (aggregationPolicy !== METRIC_AGGREGATION_POLICIES.SUM) {
    return validRows.map((row) => ({ ...row }));
  }

  const grouped = new Map();
  validRows.forEach((row, sourceIndex) => {
    const categoryKey = normalizeCategoryValue(viewBy ? row?.[viewBy] : "overall");
    const temporalKey =
      mode === "temporal"
        ? normalizeCategoryValue(row[temporalField])
        : "single-period";
    const key = temporalKey + "\u0000" + categoryKey;
    const current = grouped.get(key);
    if (current) {
      current[metric] += row[metric];
      return;
    }
    grouped.set(key, {
      ...(viewBy ? { [viewBy]: row[viewBy] } : {}),
      ...(mode === "temporal" ? { [temporalField]: row[temporalField] } : {}),
      [metric]: row[metric],
      __sourceIndex: sourceIndex,
    });
  });
  return Array.from(grouped.values());
};

const hashIdentity = (identity) => {
  let hash = 2166136261;
  for (let index = 0; index < identity.length; index += 1) {
    hash ^= identity.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export const getSeriesIdentity = (field, value) =>
  `${normalizeFieldName(field)}:${normalizeCategoryValue(value)}`;

export const getStableSeriesColor = (field, value) =>
  getFallbackSeriesColor(field, value);

export const getStableSeriesDataKey = (field, value) =>
  `series_${hashIdentity(getSeriesIdentity(field, value)).toString(36)}`;

export const getCategorySpacing = (metricCount) => {
  const { rowHeight, barSize, barGap, categoryGap } = getCategoricalLayout({
    categoryCount: 1,
    metricCount,
  });
  return { rowHeight, barSize, barGap, categoryGap };
};

export const getCategoricalChartHeight = (
  categoryCount,
  metricCount,
  orientation = "horizontal",
) => getCategoricalLayout({ categoryCount, metricCount, orientation }).chartHeight;

export const getCategoricalOrientation = ({
  labels,
  automaticCountryComparison = false,
}) => {
  if (!automaticCountryComparison) return "horizontal";
  const safeLabels = (Array.isArray(labels) ? labels : []).map((label) =>
    String(label ?? "").trim(),
  );
  const longest = safeLabels.reduce(
    (length, label) => Math.max(length, label.length),
    0,
  );
  return safeLabels.length <= 5 && longest <= 14 ? "vertical" : "horizontal";
};

export const buildChartModel = ({
  rows,
  mode,
  viewBy,
  metric,
  metrics,
  temporalField,
  preserveBackendOrder = false,
  automaticCountryComparison = false,
  aggregationPolicy = METRIC_AGGREGATION_POLICIES.NONE,
  aggregationPolicies = {},
  metricLabels = {},
  tooltipContextFields = [],
}) => {
  const availableMetrics = (Array.isArray(metrics) && metrics.length
    ? metrics
    : [metric]
  ).filter(Boolean);
  const activeMetric = metric || availableMetrics[0];
  const validRows = buildVisualizationDataset({
    rows,
    mode,
    viewBy,
    metric: activeMetric,
    temporalField,
    aggregationPolicy:
      aggregationPolicies[activeMetric] || aggregationPolicy,
  });
  const categories = viewBy
    ? getMeaningfulUniqueValues(validRows, viewBy)
    : ["Overall"];
  const orderedCategories =
    mode === "temporal" && viewBy
      ? categories.slice().sort((left, right) =>
          String(left).localeCompare(String(right), undefined, { sensitivity: "base" }),
        )
      : categories;
  const categorySeries = orderedCategories.map((value, index) => ({
    value,
    identity: getSeriesIdentity(viewBy || "overall", value),
    dataKey: getStableSeriesDataKey(viewBy || "overall", value),
    color: getTemporalSeriesColor(viewBy || "overall", value, index),
  }));

  if (mode === "temporal") {
    const timeValues = getMeaningfulUniqueValues(validRows, temporalField).sort(
      (left, right) =>
        parseTemporalValue(left, temporalField) - parseTemporalValue(right, temporalField),
    );
    const data = timeValues.map((timeValue) => {
      const point = { __time: timeValue };
      categorySeries.forEach(({ value, dataKey }) => {
        const row = validRows.find(
          (candidate) =>
            (!viewBy ||
              normalizeCategoryValue(candidate?.[viewBy]) ===
                normalizeCategoryValue(value)) &&
            normalizeCategoryValue(candidate?.[temporalField]) ===
              normalizeCategoryValue(timeValue),
        );
        if (row) point[dataKey] = row[activeMetric];
      });
      const sourceRows = rows.filter(
        (row) =>
          normalizeCategoryValue(row?.[temporalField]) ===
          normalizeCategoryValue(timeValue),
      );
      point.__context = tooltipContextFields.flatMap((field) => {
        const values = getMeaningfulUniqueValues(sourceRows, field);
        return values.length === 1 ? [{ field, value: values[0] }] : [];
      });
      return point;
    });
    return {
      data,
      series: categorySeries,
      hasBreakdown: Boolean(viewBy),
      orientation: null,
    };
  }

  const metricColors = getMetricSeriesColors(availableMetrics);
  const metricSeries = availableMetrics.map((field, index) => ({
    value: field,
    label: metricLabels[field] || formatFieldLabel(field),
    identity: getSeriesIdentity("metric", field),
    dataKey: getStableSeriesDataKey("metric", field),
    color: metricColors[index],
  }));
  const metricRows = new Map(
    metricSeries.map(({ value }) => [
      value,
      buildVisualizationDataset({
        rows,
        mode,
        viewBy,
        metric: value,
        temporalField,
        aggregationPolicy:
          aggregationPolicies[value] ||
          (value === activeMetric
            ? aggregationPolicy
            : METRIC_AGGREGATION_POLICIES.NONE),
      }),
    ]),
  );
  const orderedCategoryValues = getMeaningfulUniqueValues(rows, viewBy).filter(
    (category) =>
      metricSeries.some(({ value }) =>
        metricRows.get(value).some(
          (row) =>
            normalizeCategoryValue(row?.[viewBy]) ===
            normalizeCategoryValue(category),
        ),
      ),
  );
  const data = orderedCategoryValues.map((category) => {
    const point = { label: category };
    metricSeries.forEach(({ value, dataKey }) => {
      const row = metricRows.get(value).find(
        (candidate) =>
          normalizeCategoryValue(candidate?.[viewBy]) ===
          normalizeCategoryValue(category),
      );
      if (row) point[dataKey] = row[value];
    });
    return point;
  });
  if (!preserveBackendOrder) {
    const primaryDataKey = metricSeries[0]?.dataKey;
    data.sort(
      (left, right) =>
        (Number.isFinite(right[primaryDataKey]) ? right[primaryDataKey] : -Infinity) -
          (Number.isFinite(left[primaryDataKey]) ? left[primaryDataKey] : -Infinity) ||
        String(left.label).localeCompare(String(right.label), undefined, {
          sensitivity: "base",
        }),
    );
  }
  return {
    data,
    series: metricSeries,
    orientation: getCategoricalOrientation({
      labels: data.map(({ label }) => label),
      automaticCountryComparison,
    }),
  };
};
export const formatMetricValue = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "";

export const formatTemporalLabel = (value) => {
  const text = String(value ?? "");
  return text.includes("T") ? text.slice(0, 10) : text;
};

export { FIELD_TYPES, METRIC_AGGREGATION_POLICIES };
